// src/pages/Engagement.jsx
// Engagement campaigns: pick a mail, see what every customer would get (real
// numbers, or why they are skipped), test-send to yourself, send to a
// selection, and read what came back through the mails' answer links.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGraphQL } from '../graphql';
import { useAuth } from '../components/AuthWrapper';
import Modal from '../components/Modal';
import {
    MegaphoneIcon,
    ArrowPathIcon,
    PaperAirplaneIcon,
    BeakerIcon,
    MoonIcon,
} from '@heroicons/react/24/outline';

// The feature catalog ships next to its images on the public site (frontend
// public/email-assets/features.json) — one source for picture and copy.
const FEATURE_CATALOG_URL = 'https://www.bidlyzer.com/email-assets/features.json';

const SKIP_LABELS = {
    OPTED_OUT: 'abgemeldet',
    COOLDOWN: 'kürzlich erhalten',
    NO_FIRST_NAME: 'Vorname fehlt',
    NO_MATCHES: 'keine passenden Treffer',
    HAS_PLAN: 'zahlt oder testet',
    PAYING: 'zahlt bereits',
};

// The numbers worth seeing per campaign before sending.
const SUMMARY = {
    'engagement.missed_matches': (d) => `${d.MISSED_COUNT} neu · ${d.CLOSING_SOON_COUNT} bald fällig`,
    'engagement.usage_recap_free': (d) => `${d.MATCH_COUNT} Treffer · ${d.MATCH_VALUE}`,
    'engagement.winback_offer': (d) => (d.DISCOUNT_PERCENT ? `${d.DISCOUNT_PERCENT} % · ${d.DISCOUNT_DURATION}` : '—'),
    'engagement.price_relaunch': (d) => (d.DISCOUNT_PERCENT ? `Pro ${d.NEW_PRICE_PRO} · ${d.DISCOUNT_PERCENT} % · ${d.DISCOUNT_DURATION}` : '—'),
    'engagement.sunset': (d) => `Pause ab ${d.SUNSET_DATE}`,
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');
const inDays = (n) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);

const Engagement = () => {
    const { executeQuery, executeMutation, queries, mutations } = useGraphQL();
    const { user } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [template, setTemplate] = useState('');
    const [catalog, setCatalog] = useState([]);
    const [features, setFeatures] = useState([]);
    const [issueMonth, setIssueMonth] = useState('');
    const [discountCode, setDiscountCode] = useState('');
    const [sunsetDate, setSunsetDate] = useState(inDays(14));
    const [recipients, setRecipients] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [sends, setSends] = useState([]);
    const [testTo, setTestTo] = useState('');
    const [force, setForce] = useState(false);
    const [busy, setBusy] = useState('');
    const [notice, setNotice] = useState(null);
    const [confirm, setConfirm] = useState(false);
    const [detail, setDetail] = useState(null);
    const [sunsetList, setSunsetList] = useState(null);

    const campaign = campaigns.find((c) => c.template === template);
    const needs = (field) => campaign?.content.includes(field);

    useEffect(() => {
        executeQuery(queries.getEngagementCampaigns()).then((d) => {
            setCampaigns(d.adminEngagementCampaigns);
            setTemplate((t) => t || d.adminEngagementCampaigns[0]?.template || '');
        });
        // no-cache: revalidate every time (ETag), so a catalog deployed a
        // minute ago shows up on the next reload, not after max-age.
        fetch(FEATURE_CATALOG_URL, { cache: 'no-cache' }).then((r) => (r.ok ? r.json() : [])).then(setCatalog).catch(() => setCatalog([]));
    }, [executeQuery, queries]);

    useEffect(() => {
        if (user?.email) setTestTo((t) => t || user.email);
    }, [user]);

    const content = useMemo(() => ({
        issueMonth: issueMonth || undefined,
        features: features.map(({ title, text, url, imageUrl }) => ({ title, text, url, imageUrl })),
        discountCode: discountCode || undefined,
        sunsetDate: sunsetDate ? new Date(`${sunsetDate}T00:00:00`).toISOString() : undefined,
    }), [issueMonth, features, discountCode, sunsetDate]);

    const loadPreview = useCallback(async () => {
        if (!template) return;
        setBusy('preview');
        setNotice(null);
        try {
            const d = await executeQuery(queries.getEngagementPreview(), { template, content });
            setRecipients(d.adminEngagementPreview);
            // Nothing is preselected: a real send is a deliberate choice. A
            // refresh keeps that choice for everyone who is still sendable.
            const ready = new Set(d.adminEngagementPreview.filter((r) => !r.skip).map((r) => r.userId));
            setSelected((cur) => new Set([...cur].filter((id) => ready.has(id))));
        } catch (e) {
            setRecipients([]);
            setNotice({ error: e.message });
        } finally {
            setBusy('');
        }
    }, [template, content, executeQuery, queries]);

    const loadSends = useCallback(async () => {
        if (!template) return;
        const d = await executeQuery(queries.getEngagementSends(), { template, limit: 200 });
        setSends(d.adminEngagementSends);
    }, [template, executeQuery, queries]);

    // Content edits refresh on demand ("Vorschau"), a campaign switch at once.
    useEffect(() => {
        setSunsetList(null);
        loadPreview();
        loadSends();
    }, [template]);

    const toggleFeature = (f) => setFeatures((cur) => {
        if (cur.some((x) => x.slug === f.slug)) return cur.filter((x) => x.slug !== f.slug);
        return cur.length < 3 ? [...cur, { ...f }] : cur;
    });

    const send = async (test) => {
        setConfirm(false);
        setBusy(test ? 'test' : 'send');
        setNotice(null);
        try {
            const d = await executeMutation(mutations.sendEngagementMail(), {
                input: {
                    template,
                    userIds: [...selected],
                    content,
                    testTo: test ? testTo : undefined,
                    force,
                },
            });
            const r = d.adminSendEngagementMail;
            if (!r.success) {
                setNotice({ error: r.error });
            } else {
                setNotice({
                    ok: `${r.sent.length} ${test ? `Testmail(s) an ${testTo}` : 'Mail(s)'} in der Queue` +
                        (r.skipped.length ? ` · ${r.skipped.length} übersprungen (${r.skipped.map((s) => `${s.email}: ${SKIP_LABELS[s.skip] || s.skip}`).join(', ')})` : ''),
                });
                loadPreview();
                loadSends();
            }
        } catch (e) {
            setNotice({ error: e.message });
        } finally {
            setBusy('');
        }
    };

    const runSunset = async (dryRun) => {
        setBusy('sunset');
        try {
            const d = await executeMutation(mutations.applyEngagementSunset(), { dryRun });
            const r = d.adminApplyEngagementSunset;
            setSunsetList({ dryRun, paused: r.paused, error: r.error });
        } finally {
            setBusy('');
        }
    };

    const sendable = recipients.filter((r) => !r.skip);
    const summary = SUMMARY[template];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <MegaphoneIcon className="h-8 w-8 text-primary" />
                    <div>
                        <h1 className="text-2xl font-bold text-white">Engagement</h1>
                        <p className="text-gray-400 text-sm">Kampagnen-Mails an ausgewählte Kunden — mit echten Zahlen je Nutzer</p>
                    </div>
                </div>
                <button onClick={() => { loadPreview(); loadSends(); }} disabled={!!busy}
                    className="px-4 py-2 bg-gray-700 text-white hover:bg-gray-600 flex items-center space-x-2">
                    <ArrowPathIcon className={`h-4 w-4 ${busy === 'preview' ? 'animate-spin' : ''}`} />
                    <span>Aktualisieren</span>
                </button>
            </div>

            <div className="flex flex-wrap gap-2">
                {campaigns.map((c) => (
                    <button key={c.template} onClick={() => setTemplate(c.template)}
                        className={`px-3 py-2 text-sm ${c.template === template ? 'bg-primary text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>
                        {c.label}
                        <span className="ml-2 text-xs opacity-70">{c.cooldownDays} T Pause</span>
                    </button>
                ))}
            </div>

            {campaign && campaign.content.length > 0 && (
                <div className="bg-gray-800 p-4 space-y-4">
                    <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Inhalt</h2>
                    {needs('issueMonth') && (
                        <label className="block text-sm text-gray-400">Ausgabe (Monat)
                            <input value={issueMonth} onChange={(e) => setIssueMonth(e.target.value)} placeholder="aktueller Monat"
                                className="mt-1 block w-60 bg-gray-900 text-white px-3 py-2 border border-gray-700" />
                        </label>
                    )}
                    {needs('features') && (
                        <div>
                            <p className="text-sm text-gray-400 mb-2">Genau drei Features wählen ({features.length}/3) — Reihenfolge = Klickreihenfolge</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                                {catalog.map((f) => {
                                    const pos = features.findIndex((x) => x.slug === f.slug);
                                    return (
                                        <button key={f.slug} onClick={() => toggleFeature(f)}
                                            className={`text-left bg-gray-900 border-2 ${pos >= 0 ? 'border-primary' : 'border-transparent'} hover:border-gray-600`}>
                                            <img src={f.imageUrl} alt="" className="w-full block" />
                                            <div className="p-2 text-xs text-white">
                                                {pos >= 0 && <span className="mr-1 px-1 bg-primary text-white">{pos + 1}</span>}
                                                {f.title}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {features.map((f, i) => (
                                <div key={f.slug} className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-2">
                                    <input value={f.title} onChange={(e) => setFeatures((cur) => cur.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                                        className="bg-gray-900 text-white px-3 py-2 border border-gray-700 text-sm" />
                                    <input value={f.text} onChange={(e) => setFeatures((cur) => cur.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                                        className="bg-gray-900 text-white px-3 py-2 border border-gray-700 text-sm" />
                                </div>
                            ))}
                        </div>
                    )}
                    {needs('discountCode') && (
                        <label className="block text-sm text-gray-400">Stripe-Promotion-Code (aktiv, mit Ablaufdatum)
                            <input value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="z. B. COMEBACK50"
                                className="mt-1 block w-60 bg-gray-900 text-white px-3 py-2 border border-gray-700 font-mono" />
                        </label>
                    )}
                    {needs('sunsetDate') && (
                        <label className="block text-sm text-gray-400">Pausieren ab
                            <input type="date" value={sunsetDate} min={inDays(1)} onChange={(e) => setSunsetDate(e.target.value)}
                                className="mt-1 block w-60 bg-gray-900 text-white px-3 py-2 border border-gray-700" />
                        </label>
                    )}
                    <button onClick={loadPreview} disabled={!!busy} className="px-4 py-2 bg-gray-700 text-white hover:bg-gray-600">
                        Vorschau mit diesem Inhalt
                    </button>
                </div>
            )}

            {notice && (
                <div className={`p-3 text-sm ${notice.error ? 'bg-red-900 text-red-100' : 'bg-green-900 text-green-100'}`}>
                    {notice.error || notice.ok}
                </div>
            )}

            <div className="bg-gray-800">
                <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-700">
                    <span className="text-white text-sm font-semibold">{selected.size} ausgewählt</span>
                    <span className="text-gray-400 text-sm">{sendable.length} von {recipients.length} versandbereit</span>
                    <div className="flex-1" />
                    <input value={testTo} onChange={(e) => setTestTo(e.target.value)} className="bg-gray-900 text-white px-3 py-2 border border-gray-700 text-sm w-64" />
                    <button onClick={() => send(true)} disabled={!!busy || selected.size === 0 || !testTo}
                        className="px-4 py-2 bg-gray-700 text-white hover:bg-gray-600 flex items-center space-x-2 disabled:opacity-50">
                        <BeakerIcon className="h-4 w-4" /><span>Testversand an mich</span>
                    </button>
                    <label className="text-sm text-gray-300 flex items-center gap-2">
                        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> Pause ignorieren
                    </label>
                    <button onClick={() => setConfirm(true)} disabled={!!busy || selected.size === 0}
                        className="px-4 py-2 bg-primary text-white hover:bg-opacity-90 flex items-center space-x-2 disabled:opacity-50">
                        <PaperAirplaneIcon className="h-4 w-4" /><span>An Auswahl senden ({selected.size})</span>
                    </button>
                </div>
                <table className="w-full text-sm">
                    <thead className="text-gray-400 text-left">
                        <tr>
                            <th className="p-3 w-8">
                                <input type="checkbox" checked={sendable.length > 0 && sendable.every((r) => selected.has(r.userId))}
                                    onChange={(e) => setSelected(e.target.checked ? new Set(sendable.map((r) => r.userId)) : new Set())} />
                            </th>
                            <th className="p-3">Empfänger</th>
                            <th className="p-3">Firma</th>
                            <th className="p-3">Plan</th>
                            <th className="p-3">Letzter Login</th>
                            <th className="p-3">Zuletzt erhalten</th>
                            <th className="p-3">Status</th>
                            <th className="p-3">Inhalt</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recipients.map((r) => (
                            <tr key={r.userId} className="border-t border-gray-700 hover:bg-gray-750">
                                <td className="p-3">
                                    <input type="checkbox" checked={selected.has(r.userId)}
                                        onChange={(e) => setSelected((cur) => {
                                            const next = new Set(cur);
                                            if (e.target.checked) next.add(r.userId); else next.delete(r.userId);
                                            return next;
                                        })} />
                                </td>
                                <td className="p-3">
                                    <div className="text-white">{r.email}</div>
                                    <div className="text-gray-400 text-xs">{r.firstName} {r.lastName}</div>
                                </td>
                                <td className="p-3 text-white">{r.companyName}</td>
                                <td className="p-3 text-gray-300 font-mono text-xs">{r.tier}/{r.status}</td>
                                <td className="p-3 text-gray-300">{fmtDate(r.lastLogin)}</td>
                                <td className="p-3 text-gray-300">{fmtDate(r.lastSentAt)}</td>
                                <td className="p-3">
                                    {r.skip
                                        ? <span className="px-2 py-0.5 bg-gray-700 text-gray-300 text-xs">{SKIP_LABELS[r.skip] || r.skip}</span>
                                        : <span className="px-2 py-0.5 bg-green-900 text-green-200 text-xs">bereit</span>}
                                </td>
                                <td className="p-3">
                                    <button onClick={() => setDetail(r)} className="text-primary text-xs underline">
                                        {summary && !r.skip ? summary(r.data) : 'Daten'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {recipients.length === 0 && (
                            <tr><td colSpan={8} className="p-6 text-center text-gray-500">{busy === 'preview' ? 'Lade …' : 'Keine Empfänger'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {template === 'engagement.sunset' && (
                <div className="bg-gray-800 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <MoonIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-white text-sm font-semibold">Sunset vollziehen</span>
                        <span className="text-gray-400 text-sm">pausiert alle, deren Frist ohne „bleiben“ und ohne Login abgelaufen ist</span>
                        <div className="flex-1" />
                        <button onClick={() => runSunset(true)} disabled={!!busy} className="px-3 py-2 bg-gray-700 text-white text-sm">Prüfen</button>
                        {sunsetList?.dryRun && sunsetList.paused.length > 0 && (
                            <button onClick={() => runSunset(false)} disabled={!!busy} className="px-3 py-2 bg-red-700 text-white text-sm">
                                {sunsetList.paused.length} jetzt pausieren
                            </button>
                        )}
                    </div>
                    {sunsetList && (
                        <p className="text-sm text-gray-300">
                            {sunsetList.error || (sunsetList.paused.length === 0 ? 'Niemand fällig.' :
                                `${sunsetList.dryRun ? 'Würde pausieren' : 'Pausiert'}: ${sunsetList.paused.join(', ')}`)}
                        </p>
                    )}
                </div>
            )}

            <div className="bg-gray-800">
                <h2 className="p-4 text-sm font-semibold text-gray-300 uppercase tracking-wide border-b border-gray-700">Versendet &amp; Antworten</h2>
                <table className="w-full text-sm">
                    <thead className="text-gray-400 text-left">
                        <tr><th className="p-3">Gesendet</th><th className="p-3">An</th><th className="p-3">Antwort</th><th className="p-3">Rückmeldung</th></tr>
                    </thead>
                    <tbody>
                        {sends.map((s) => (
                            <tr key={s.id} className="border-t border-gray-700 align-top">
                                <td className="p-3 text-gray-300">{new Date(s.sentAt).toLocaleString('de-DE')}</td>
                                <td className="p-3 text-white">{s.recipient}</td>
                                <td className="p-3 text-white">{s.answer || <span className="text-gray-500">—</span>}
                                    {s.answeredAt && <div className="text-gray-400 text-xs">{fmtDate(s.answeredAt)}</div>}</td>
                                <td className="p-3 text-gray-300 text-xs whitespace-pre-wrap">
                                    {s.feedback ? Object.entries(s.feedback).map(([k, v]) => `${k}: ${v}`).join('\n') : ''}
                                </td>
                            </tr>
                        ))}
                        {sends.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-gray-500">Noch nichts versendet</td></tr>}
                    </tbody>
                </table>
            </div>

            <Modal isOpen={confirm} onClose={() => setConfirm(false)} title="Wirklich senden?" size="sm">
                <p className="text-gray-300 text-sm">
                    {selected.size} echte E-Mail(s) „{campaign?.label}“ an Kunden. Abgemeldete und Nutzer ohne Daten werden übersprungen{force ? '' : ', ebenso wer die Mail kürzlich bekam'}.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                    <button onClick={() => setConfirm(false)} className="px-4 py-2 bg-gray-700 text-white">Abbrechen</button>
                    <button onClick={() => send(false)} className="px-4 py-2 bg-primary text-white">Senden</button>
                </div>
            </Modal>

            <Modal isOpen={!!detail} onClose={() => setDetail(null)} title={detail?.email} size="xl">
                <pre className="text-xs text-gray-200 whitespace-pre-wrap break-all max-h-[60vh] overflow-auto">
                    {detail && JSON.stringify(detail.data, null, 2)}
                </pre>
            </Modal>
        </div>
    );
};

export default Engagement;
